import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { organisationApi } from '@/api/organisation';
import { ApiError } from '@/api/client';
import type { AsyncState } from '@/hooks';
import type { EducationSubsystem, InstitutionPositionView, PositionGroup } from '@/types/organisation';
import { POSITION_GROUPS, groupForCategory } from '@/types/organisation';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import { useToast } from '@/components/ui/Toast';
import styles from '../AdminOrganisationPage.module.css';

const NS = 'admin.organisation';

/**
 * The Positions tab — the office catalogue as THIS institution sees it.
 *
 * Two writes live here, and both are label-or-flag writes only:
 *
 *   Enable / disable — the institution's own opinion of a shared catalogue entry.
 *     `enabled` is genuinely three-valued: true, false, and null ("no opinion,
 *     follow the catalogue"). The switch renders null as indeterminate
 *     (aria-checked="mixed"), never as ON — showing a defaulted value as a chosen
 *     one is how administrators end up "turning off" something they never turned on.
 *     When the catalogue itself retired the position (catalogueActive === false)
 *     the switch is disabled with that stated as the reason: an institution cannot
 *     revive what the catalogue withdrew.
 *
 *   Rename — a per-institution DISPLAY title. The canonical code never changes and
 *     is shown, unchanged, inside the rename dialog so the administrator can see
 *     exactly what is NOT being edited. The dialog also names the current title's
 *     source (their own override / shared label / template default) — you should
 *     know whose words you are overwriting.
 */

interface PositionsTabProps {
  institutionId: string;
  locale: string;
  subsystem: EducationSubsystem;
  institutionType: string;
  profileMissing: boolean;
  state: AsyncState<InstitutionPositionView[]>;
  onRetry: () => void;
  onChanged: () => void;
}

type EnableDialog = { position: InstitutionPositionView; nextEnabled: boolean };
type RenameDialog = { position: InstitutionPositionView };

export function PositionsTab({
  institutionId,
  locale,
  subsystem,
  state,
  onRetry,
  onChanged,
}: PositionsTabProps) {
  const { t } = useTranslation();
  const [enableDialog, setEnableDialog] = useState<EnableDialog | null>(null);
  const [renameDialog, setRenameDialog] = useState<RenameDialog | null>(null);

  const positions = state.status === 'ready' ? state.data : [];

  const grouped = useMemo(() => {
    const map = new Map<PositionGroup, InstitutionPositionView[]>();
    for (const position of positions) {
      const group = groupForCategory(position.category);
      const bucket = map.get(group);
      if (bucket) bucket.push(position);
      else map.set(group, [position]);
    }
    for (const bucket of map.values()) bucket.sort((a, b) => a.displayTitle.localeCompare(b.displayTitle));
    return map;
  }, [positions]);

  return (
    <section className={styles.section}>
      <AsyncPanel
        state={state}
        onRetry={onRetry}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t(`${NS}.positions.empty`)}
        emptyBody={t(`${NS}.positions.emptyBody`)}
      >
        {() =>
          POSITION_GROUPS.flatMap((group) => {
            const bucket = grouped.get(group);
            if (!bucket || bucket.length === 0) return [];
            return [
              <Card key={group} className={styles.groupCard}>
                <header className={styles.groupHeader}>
                  <h3 className={styles.groupTitle}>{t(`${NS}.groups.${group}`)}</h3>
                  <span className={styles.groupCount}>
                    {t(`${NS}.positions.count`, { total: bucket.length })}
                  </span>
                </header>
                <ul className={styles.positionList}>
                  {bucket.map((position) => (
                    <PositionRow
                      key={position.id}
                      position={position}
                      onToggle={(nextEnabled) => setEnableDialog({ position, nextEnabled })}
                      onRename={() => setRenameDialog({ position })}
                    />
                  ))}
                </ul>
              </Card>,
            ];
          })
        }
      </AsyncPanel>

      {enableDialog && (
        <EnableDialogModal
          institutionId={institutionId}
          locale={locale}
          subsystem={subsystem}
          position={enableDialog.position}
          nextEnabled={enableDialog.nextEnabled}
          onClose={() => setEnableDialog(null)}
          onDone={() => {
            setEnableDialog(null);
            onChanged();
          }}
        />
      )}

      {renameDialog && (
        <RenameDialogModal
          institutionId={institutionId}
          locale={locale}
          subsystem={subsystem}
          position={renameDialog.position}
          onClose={() => setRenameDialog(null)}
          onDone={() => {
            setRenameDialog(null);
            onChanged();
          }}
        />
      )}
    </section>
  );
}

function PositionRow({
  position,
  onToggle,
  onRename,
}: {
  position: InstitutionPositionView;
  onToggle: (nextEnabled: boolean) => void;
  onRename: () => void;
}) {
  const { t } = useTranslation();

  // The switch's three faces. null renders as "mixed" — the institution has no
  // recorded opinion and the catalogue's answer applies.
  const checked: boolean | 'mixed' = position.enabled === null ? 'mixed' : position.enabled;
  const catalogueRetired = !position.catalogueActive;

  return (
    <li className={styles.positionRow}>
      <div className={styles.positionMain}>
        <span className={styles.positionTitle}>{position.displayTitle}</span>
        {/* The code sits beside the title on purpose: an administrator renaming the
            title has to see what is NOT changing. */}
        <code className={styles.canonicalCode} title={t(`${NS}.positions.codeHint`)}>
          {position.canonicalCode}
        </code>
        <Badge tone={position.titleSource === 'INSTITUTION' ? 'info' : 'neutral'}>
          {t(`${NS}.titleSources.${position.titleSource}`)}
        </Badge>
        {!position.active && <Badge tone="neutral">{t(`${NS}.positions.inactive`)}</Badge>}
        {position.requiresApproval && <Badge tone="warning">{t(`${NS}.positions.requiresApproval`)}</Badge>}
        {position.systemManaged && <Badge tone="info">{t(`${NS}.positions.systemManaged`)}</Badge>}
      </div>
      {position.description && <p className={styles.positionDescription}>{position.description}</p>}
      <div className={styles.positionMeta}>
        <span>
          {t(`${NS}.positions.category`)}:{' '}
          {t(`${NS}.categories.${position.category}`, { defaultValue: position.category })}
        </span>
        <span>
          {t(`${NS}.positions.scopes`)}:{' '}
          {position.assignableScopeTypes.length > 0
            ? position.assignableScopeTypes
                .map((scope) => t(`${NS}.scopeTypes.${scope}`, { defaultValue: scope }))
                .join(' · ')
            : t(`${NS}.positions.noScopes`)}
        </span>
      </div>
      <div className={styles.positionActions}>
        <span className={styles.enableControl}>
          <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={t(`${NS}.positions.enabledSwitchLabel`, { title: position.displayTitle })}
            className={`${styles.triSwitch} ${
              checked === 'mixed' ? styles.triSwitchMixed : checked ? styles.triSwitchOn : styles.triSwitchOff
            }`}
            disabled={catalogueRetired}
            title={catalogueRetired ? t(`${NS}.positions.catalogueRetired`) : undefined}
            onClick={() => {
              // A mixed switch flips to the OPPOSITE of what currently applies, which
              // (since enabled=null falls back to the catalogue and catalogueActive is
              // true here) means recording an explicit "off".
              const currentlyOn = position.enabled === null ? position.catalogueActive : position.enabled;
              onToggle(!currentlyOn);
            }}
          >
            <span className={styles.triSwitchThumb} aria-hidden="true" />
          </button>
          <span className={styles.enableStateLabel}>
            {catalogueRetired
              ? t(`${NS}.positions.catalogueRetired`)
              : position.enabled === null
                ? t(`${NS}.positions.enabledDefault`)
                : position.enabled
                  ? t(`${NS}.positions.enabledOn`)
                  : t(`${NS}.positions.enabledOff`)}
          </span>
        </span>
        <button type="button" className={styles.ghostAction} onClick={onRename}>
          {t(`${NS}.positions.renameTitle`)}
        </button>
      </div>
    </li>
  );
}

/**
 * Confirm-with-reason for enable/disable.
 *
 * A modal rather than an instant flip because there is no undo endpoint: the write is
 * cheap but its consequence (an office disappearing from the appointment form for the
 * whole institution) is not, and the optional reason lands in the audit trail.
 */
function EnableDialogModal({
  institutionId,
  locale,
  subsystem,
  position,
  nextEnabled,
  onClose,
  onDone,
}: {
  institutionId: string;
  locale: string;
  subsystem: EducationSubsystem;
  position: InstitutionPositionView;
  nextEnabled: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await organisationApi.positions.setEnabled(institutionId, position.canonicalCode, {
        enabled: nextEnabled,
        reason: reason.trim() || undefined,
        locale,
        subsystem,
      });
      toast.success(
        nextEnabled
          ? t(`${NS}.positions.enabledToast`, { title: position.displayTitle })
          : t(`${NS}.positions.disabledToast`, { title: position.displayTitle }),
      );
      onDone();
    } catch (cause) {
      setError(describeWriteFailure(cause, t));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title={
        nextEnabled
          ? t(`${NS}.positions.enableConfirmTitle`, { title: position.displayTitle })
          : t(`${NS}.positions.disableConfirmTitle`, { title: position.displayTitle })
      }
      description={
        nextEnabled ? t(`${NS}.positions.enableConfirmBody`) : t(`${NS}.positions.disableConfirmBody`)
      }
      onClose={onClose}
    >
      <div className={styles.dialogBody}>
        {error && <Alert tone="error">{error}</Alert>}
        <FormField
          id="position-enable-reason"
          label={t(`${NS}.positions.reasonLabel`)}
          hint={t(`${NS}.positions.reasonHint`)}
        >
          <input
            id="position-enable-reason"
            className={styles.textInput}
            type="text"
            value={reason}
            maxLength={500}
            onChange={(event) => setReason(event.target.value)}
          />
        </FormField>
        <div className={styles.dialogActions}>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant={nextEnabled ? 'primary' : 'danger'}
            onClick={submit}
            loading={busy}
            loadingLabel={t('common.submitting')}
          >
            {nextEnabled ? t(`${NS}.positions.enable`) : t(`${NS}.positions.disable`)}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Rename — a per-institution display title in one locale. */
function RenameDialogModal({
  institutionId,
  locale,
  subsystem,
  position,
  onClose,
  onDone,
}: {
  institutionId: string;
  locale: string;
  subsystem: EducationSubsystem;
  position: InstitutionPositionView;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [titleLocale, setTitleLocale] = useState(locale === 'fr' ? 'fr' : 'en');
  const [title, setTitle] = useState(position.displayTitle);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = title.trim();

  const submit = async () => {
    if (!trimmed) {
      setError(t(`${NS}.positions.renameEmpty`));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await organisationApi.positions.setTitle(institutionId, position.canonicalCode, {
        locale: titleLocale,
        displayTitle: trimmed,
        reason: reason.trim() || undefined,
        subsystem,
      });
      toast.success(t(`${NS}.positions.renamedToast`, { title: trimmed }));
      onDone();
    } catch (cause) {
      setError(describeWriteFailure(cause, t));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title={t(`${NS}.positions.renameDialogTitle`, { title: position.displayTitle })}
      description={t(`${NS}.positions.renameDialogBody`)}
      onClose={onClose}
    >
      <div className={styles.dialogBody}>
        {error && <Alert tone="error">{error}</Alert>}

        {/* What is being overridden, and what is not. */}
        <p className={styles.renameContext}>
          {t(`${NS}.positions.renameCurrentSource`, {
            source: t(`${NS}.titleSources.${position.titleSource}`),
          })}{' '}
          <code className={styles.canonicalCode} title={t(`${NS}.positions.codeHint`)}>
            {position.canonicalCode}
          </code>{' '}
          {t(`${NS}.positions.renameCodeUnchanged`)}
        </p>

        <FormField id="rename-locale" label={t(`${NS}.positions.renameLocale`)} required>
          <Select
            id="rename-locale"
            value={titleLocale}
            onChange={setTitleLocale}
            options={[
              { value: 'en', label: t(`${NS}.positions.localeEn`) },
              { value: 'fr', label: t(`${NS}.positions.localeFr`) },
            ]}
          />
        </FormField>

        <FormField id="rename-title" label={t(`${NS}.positions.renameNewTitle`)} required>
          <input
            id="rename-title"
            className={styles.textInput}
            type="text"
            value={title}
            maxLength={200}
            onChange={(event) => setTitle(event.target.value)}
          />
        </FormField>

        <FormField
          id="rename-reason"
          label={t(`${NS}.positions.reasonLabel`)}
          hint={t(`${NS}.positions.reasonHint`)}
        >
          <input
            id="rename-reason"
            className={styles.textInput}
            type="text"
            value={reason}
            maxLength={500}
            onChange={(event) => setReason(event.target.value)}
          />
        </FormField>

        <div className={styles.dialogActions}>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} loading={busy} loadingLabel={t('common.submitting')} disabled={!trimmed}>
            {t(`${NS}.positions.renameSave`)}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Words a failed organisation write, keeping identity's distinct failures distinct.
 *
 * 403 CROSS_TENANT_ACCESS_DENIED and 409 CONTEXT_REQUIRED both arrive with server
 * sentences that actually explain themselves ("you hold no live assignment in this
 * tenant", "you act in several tenants — pick one"), so the server message is shown
 * verbatim when present; the fallback names the failure class instead of shrugging.
 */
export function describeWriteFailure(cause: unknown, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (cause instanceof ApiError) {
    if (cause.status === 409) return cause.message || t(`${NS}.errors.contextRequired`);
    if (cause.status === 403) return cause.message || t(`${NS}.errors.forbidden`);
    if (cause.status === 400) return cause.message || t(`${NS}.errors.rejected`);
    if (cause.isUnreachable) return t(`${NS}.errors.unreachable`);
    return cause.message || t(`${NS}.errors.generic`);
  }
  return cause instanceof Error && cause.message ? cause.message : t(`${NS}.errors.generic`);
}
